import {ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "../../../app/store";
import {APIError} from "../../../services/HttpService";
import {cacheClearJobStatus, cacheClearJob} from "../../../services/UrlsService";
import {createDefaultMatchers, SliceStatus} from "../../BaseState";
import {RejectError, RejectErrorType} from "../../error/Error";
import {AdaptiveType} from "../UrlsState";
import DeleteUrlsState, {CacheClearJobStatus} from "./DeleteUrlsState";

const initialState: DeleteUrlsState = {
  like: "",
  regex: "",
  adaptiveType: AdaptiveType.all,
  cacheClearJobStatus: CacheClearJobStatus.idle,
  pendings: [],
  status: SliceStatus.idle,
  error: null
};

interface GetCacheClearJobStatusAction {
  readonly userId: number;
}

export const getCacheClearJobStatus = createAsyncThunk<CacheClearJobStatus, GetCacheClearJobStatusAction, {rejectValue: RejectError}>(
  "users/urls/cacheClear/jobs/status",
  async ({userId}: GetCacheClearJobStatusAction, {rejectWithValue}) => {
    try {
      const result = await cacheClearJobStatus(userId);
      return result.status;
    } catch (error) {
      if (error instanceof APIError) return rejectWithValue({message: error.message, code: error.statusCode, type: RejectErrorType.API});

      throw error;
    }
  },
  {
    condition: (action: GetCacheClearJobStatusAction, {getState}: {getState: RootState}): boolean => {
      const state = getState().deleteUrls as DeleteUrlsState;
      if (state.status === SliceStatus.loading) {
        return false;
      }

      return true;
    }
  }
);

interface CreateCacheClearJobAction {
  readonly userId: number;
  readonly filter?: string;
  readonly regex?: string;
  readonly adaptiveType?: AdaptiveType;
}

export const createCacheClearJob = createAsyncThunk<CacheClearJobStatus, CreateCacheClearJobAction, {rejectValue: RejectError}>(
  "users/urls/cacheClear/jobs/create",
  async ({userId, filter, regex, adaptiveType}: CreateCacheClearJobAction, {rejectWithValue}) => {
    try {
      const result = await cacheClearJob(userId, filter, regex, adaptiveType);
      return result.status;
    } catch (error) {
      if (error instanceof APIError) return rejectWithValue({message: error.message, code: error.statusCode, type: RejectErrorType.API});

      throw error;
    }
  },
  {
    condition: (action: CreateCacheClearJobAction, {getState}: {getState: RootState}): boolean => {
      const state = getState().deleteUrls as DeleteUrlsState;
      if (state.status === SliceStatus.loading) {
        return false;
      }

      return true;
    }
  }
);

const createActions = (builder: ActionReducerMapBuilder<DeleteUrlsState>) => {
  builder.addCase(getCacheClearJobStatus.fulfilled, (draft, action) => {
    draft.cacheClearJobStatus = action.payload;
  });
  builder.addCase(createCacheClearJob.fulfilled, (draft, action) => {
    draft.cacheClearJobStatus = action.payload;
  });

  createDefaultMatchers([getCacheClearJobStatus.typePrefix, createCacheClearJob.typePrefix], builder);
};

export const deleteUrlsSlice = createSlice({
  name: "deleteUrls",
  initialState,
  reducers: {
    setLike: (draft, action: PayloadAction<string | undefined>) => {
      draft.like = action.payload;
      draft.regex = "";
    },
    setRegex: (draft, action: PayloadAction<string | undefined>) => {
      draft.regex = action.payload;
      draft.like = "";
    },
    setAdaptiveType: (draft, action: PayloadAction<AdaptiveType>) => {
      draft.adaptiveType = action.payload;
    }
  },
  extraReducers: createActions
});

export const deleteUrlsState = (state: RootState): DeleteUrlsState => state.deleteUrls;
export const {setLike, setRegex, setAdaptiveType} = deleteUrlsSlice.actions;
export default deleteUrlsSlice.reducer;
